/**
 * API Adapter — bridges old `commands` API to new ConnectRPC SDK.
 */

import { getSDK } from '@/store/sdkStore';
import { deviceLoginInfo } from '@/lib/deviceIdentity';

function sdk() { return getSDK(); }

async function api(path: string, opts?: RequestInit): Promise<any> { return {}; }

// ── Metadata ──────────────────────────────────────────────────────────────────

const metadataCommands = {
  listCatalogs: async () => { const r = await sdk().metadata.listCatalogs({}); return r.items || []; },
  listDocuments: async () => { const r = await sdk().metadata.listDocuments({}); return r.items || []; },
  listRegisters: async () => { const r = await sdk().metadata.listRegisters({}); return r.items || []; },
  listFields: async (entityType: string, entityCode: string) => { const r = await sdk().metadata.listFields({ entityType, entityCode }); return r.items || []; },
  listTabularSections: async (entityType: string, entityCode: string) => { const r = await sdk().metadata.listTabularSections({ entityType, entityCode }); return r.items || []; },
  listTabularColumns: async (tabularSectionId: string) => { const r = await sdk().metadata.listTabularColumns({ tabularSectionId }); return r.items || []; },
  listForms: async (entityType: string, entityCode: string) => { const r = await sdk().metadata.listForms({ entityType, entityCode }); return r.items || []; },
  listModules: async (entityType: string, entityCode: string) => { const r = await sdk().metadata.listModules({ entityType, entityCode }); return r.items || []; },

  getMetadata: async () => {
    const catalogs = await sdk().metadata.listCatalogs({});
    const documents = await sdk().metadata.listDocuments({});
    const registers = await sdk().metadata.listRegisters({});
    return [...(catalogs.items || []), ...(documents.items || []), ...(registers.items || [])];
  },
  listMetadata: async () => {
    const catalogs = await sdk().metadata.listCatalogs({});
    const documents = await sdk().metadata.listDocuments({});
    return [...(catalogs.items || []), ...(documents.items || [])];
  },
  listTypes: async () => ['catalog', 'document', 'register'],
  getMetadataDetails: async (params: any) => null,
  getMetadataData: async (params: any) => ({ fields: [], rows: [] }),

  createCatalog: async (data: any) => sdk().metadata.createCatalog({ code: data.code || '', name: data.name || '', description: data.description || '' }),
  updateCatalog: async (code: string, data: any) => sdk().metadata.updateCatalog({ id: code, code: data.code || '', name: data.name || '', description: data.description || '' }),
  deleteCatalog: async (code: string) => sdk().metadata.deleteCatalog({ id: code }),

  createDocument: async (data: any) => sdk().metadata.createDocument({ code: data.code || '', name: data.name || '', description: data.description || '' }),
  updateDocument: async (code: string, data: any) => sdk().metadata.updateDocument({ id: code, code: data.code || '', name: data.name || '', description: data.description || '' }),
  deleteDocument: async (code: string) => sdk().metadata.deleteDocument({ id: code }),

  createRegister: async (data: any) => sdk().metadata.createRegister({ code: data.code || '', name: data.name || '', registerType: data.register_kind || data.registerType || 'accumulation' }),
  updateRegister: async (code: string, data: any) => sdk().metadata.updateRegister({ id: code, code: data.code || '', name: data.name || '', registerType: data.register_kind || 'accumulation' }),
  deleteRegister: async (code: string) => sdk().metadata.deleteRegister({ id: code }),

  createField: async (data: any) => sdk().metadata.createField({ code: data.name || '', name: data.name || '', entityType: data.parent_type || 'catalog', entityCode: data.parent_code || '', dataType: data.data_type || 'text' }),
  updateField: async (id: string, data: any) => sdk().metadata.updateField({ id, code: data.name || '', name: data.name || '', dataType: data.data_type || 'text' }),
  deleteField: async (id: string) => sdk().metadata.deleteField({ id }),

  createTabularSection: async (data: any) => sdk().metadata.createTabularSection({ code: data.name || '', name: data.name || '', parentType: data.parent_type || 'document', parentCode: data.parent_code || '' }),
  updateTabularSection: async (id: string, data: any) => sdk().metadata.updateTabularSection({ id, code: data.name || '', name: data.name || '' }),
  deleteTabularSection: async (id: string) => sdk().metadata.deleteTabularSection({ id }),

  createTabularColumn: async (data: any) => sdk().metadata.createTabularColumn({ code: data.name || '', name: data.name || '', tabularSectionId: data.tabular_section_id || '', dataType: data.data_type || 'text' }),
  updateTabularColumn: async (id: string, data: any) => sdk().metadata.updateTabularColumn({ id, code: data.name || '', name: data.name || '', dataType: data.data_type || 'text' }),
  deleteTabularColumn: async (id: string) => sdk().metadata.deleteTabularColumn({ id }),

  createForm: async (data: any) => sdk().metadata.createForm({ code: data.code || '', name: data.name || '', formType: data.form_type || 'object', parentType: data.parent_type || 'catalog', parentCode: data.parent_code || '' }),
  updateForm: async (id: string, data: any) => sdk().metadata.updateForm({ id, code: data.code || '', name: data.name || '', formType: data.form_type || 'object' }),
  deleteForm: async (id: string) => sdk().metadata.deleteForm({ id }),

  upsertChartOfAccounts: async (data: any) => ({ id: 'new-id' }),
  deleteChartOfAccounts: async (code: string) => {},
  upsertCalculationType: async (data: any) => ({ id: 'new-id' }),
  deleteCalculationType: async (chartCode: string, code: string) => {},

  listDocumentMapping: async (params: any) => [],
  createDocumentMapping: async (data: any) => ({ id: 'new-id' }),
  updateDocumentMapping: async (id: string, data: any) => ({ id }),
  deleteDocumentMapping: async (id: string) => {},

  upsertPostingSettings: async (data: any) => ({ id: 'new-id' }),
  deletePostingSettings: async (documentCode: string) => {},

  getFormDefs: async (params: any) => ({ forms: [] }),
  saveFormDef: async (data: any) => ({ id: 'new-id' }),
  deleteFormDef: async (id: string) => {},
  listCenterForms: async () => [],

  listObjectFormBindings: async (objectType: string, objectName: string) => [],
  saveObjectFormBinding: async (data: any) => ({ id: 'new-id' }),
  deleteObjectFormBinding: async (objectType: string, objectName: string, formType: string) => {},

  createFormBinding: async (data: any) => ({ id: 'new-id' }),
  deleteFormBinding: async (id: string) => {},

  generateDDL: async (type: string, code: string) => '',
  applyDDL: async (type: string, code: string) => {},

  fieldSynonyms: { list: async () => [], create: async (d: any) => ({}), update: async (id: string, d: any) => ({}), delete: async (id: string) => {} },
  postDocument: async (type: string, id: string) => ({ success: true }),
  unpostDocument: async (type: string, id: string) => ({ success: true }),
  getEnumerationValues: async (params: { enumerationName: string }) => {
    throw new Error('Enumerations not implemented');
  },
  callModuleFunction: async (params: { module: string; function: string; params?: any }) => {
    try {
      const r = await sdk().metadata.executeModule({ id: params.module, functionName: params.function, params: JSON.stringify(params.params || {}) });
      return r;
    } catch {
      return { success: false, error: 'Module execution not available' };
    }
  },
  getFormCommands: async (params: { formId: string }) => {
    try {
      const r = await sdk().metadata.getForm({ id: params.formId });
      return { ...r, formModule: (r as any).codeContent || (r as any).formModule || '' };
    } catch {
      return { formModule: '' };
    }
  },
  getSettings: async () => ({}),
  saveSettings: async (data: any) => ({}),
  getSectionDefs: async () => ({ sections: [] }),
  saveSectionDefs: async (data: any) => ({ id: '1' }),
  deleteSectionDefs: async (id: string) => {},
  getThemes: async () => ({ themes: [] }),
  saveThemes: async (data: any) => ({ id: '1' }),
  deleteThemes: async (id: string) => {},
};

// ── Modules ───────────────────────────────────────────────────────────────────

const modulesCommands = {
  saveObjectModule: async (data: any) => {
    if (data.id) return sdk().metadata.updateModule({ id: data.id, code: data.code || '', name: data.name || '', codeContent: data.code_content || data.codeContent || '' });
    return sdk().metadata.createModule({ code: data.code || '', name: data.name || '', moduleType: 'object', parentType: data.parent_type || 'catalog', parentCode: data.parent_code || '', codeContent: data.code_content || data.codeContent || '' });
  },
  deleteObjectModule: async (id: string) => sdk().metadata.deleteModule({ id }),
  saveManagerModule: async (data: any) => {
    if (data.id) return sdk().metadata.updateModule({ id: data.id, code: data.code || '', name: data.name || '', codeContent: data.code_content || data.codeContent || '' });
    return sdk().metadata.createModule({ code: data.code || '', name: data.name || '', moduleType: 'manager', parentType: data.parent_type || 'catalog', parentCode: data.parent_code || '', codeContent: data.code_content || data.codeContent || '' });
  },
  deleteManagerModule: async (id: string) => sdk().metadata.deleteModule({ id }),
  executeModule: async (kind: string, id: string, params: any) => sdk().metadata.executeModule({ id, functionName: params?.functionName || 'default', params: JSON.stringify(params || {}) }),
  listModules: async (kind: string) => { const r = await sdk().metadata.listModules({ entityType: kind || 'catalog', entityCode: '' }); return r.items || []; },
  getModule: async (kind: string, id: string) => sdk().metadata.getModule({ id }),
  listObjectModules: async () => { const r = await sdk().metadata.listModules({ entityType: 'catalog', entityCode: '' }); return r.items || []; },
  listManagerModules: async () => { const r = await sdk().metadata.listModules({ entityType: 'catalog', entityCode: '' }); return r.items || []; },
  executeObjectModule: async (id: string, params: any) => sdk().metadata.executeModule({ id, functionName: params?.functionName || 'default', params: JSON.stringify(params || {}) }),
  executeManagerModule: async (id: string, params: any) => sdk().metadata.executeModule({ id, functionName: params?.functionName || 'default', params: JSON.stringify(params || {}) }),
};

// ── Center ────────────────────────────────────────────────────────────────────

const centerCommands = {
  centerList: async (params: any) => ({ rows: [], total: 0 }),
  centerSave: async (params: any) => ({ id: params.id || 'new-id' }),
  centerDelete: async (params: any) => ({ success: true }),
  centerRecord: async (params: any) => ({}),
};

// ── Bank ──────────────────────────────────────────────────────────────────────

const bankCommands = {
  listBankAccounts: async () => ({ data: [] }),
  listCurrencies: async () => ({ data: [] }),
  listBanks: async () => ({ data: [] }),
  createBankAccount: async (data: any) => ({ data: { id: 'new-id' } }),
  updateBankAccount: async (id: string, data: any) => ({ data: { id } }),
  deleteBankAccount: async (id: string) => ({ data: { success: true } }),
  listDdsArticles: async () => ({ data: [] }),
  createCurrency: async (data: any) => ({ data: { id: 'new-id' } }),
  updateCurrency: async (id: string, data: any) => ({ data: { id } }),
  deleteCurrency: async (id: string) => ({ data: { success: true } }),
  createDDSArticle: async (data: any) => ({ data: { id: 'new-id' } }),
  updateDDSArticle: async (id: string, data: any) => ({ data: { id } }),
  deleteDDSArticle: async (id: string) => ({ data: { success: true } }),
  listDDSArticles: async () => ({ data: [] }),
};

// ── Other ─────────────────────────────────────────────────────────────────────

const otherCommands = {
  sections: { listSections: async () => ({ data: [] }), saveSection: async (d: any) => ({ data: { id: '1' } }), deleteSection: async (id: string) => ({ data: { success: true } }) },
  forms: { getFormDefs: async (p: any) => ({ data: [], forms: [] }), saveFormDef: async (d: any) => ({ data: { id: '1' } }), deleteFormDef: async (id: string) => ({ data: { success: true } }), getFormCommands: async () => [] },
  employees: { listEmployees: async () => ({ data: [] }), getEmployees: async () => ({ data: [] }) },
  themes: { listThemes: async () => ({ data: [] }), deleteTheme: async (id: string) => ({ success: true }) },
  auth: { loginEntities: async () => { const r = await sdk().auth.loginEntities({}); return { data: r.items || [] }; }, isAdmin: true, isAuthenticated: true, login: async (p: any) => { const r = await sdk().auth.login({ login: p.login || '', password: p.password || '', ...deviceLoginInfo() }); return { token: r.token, entity: r.entity }; } },
  sync: { listSyncJobs: async () => ({ data: [] }) },
  indicators: { listIndicators: async () => ({ data: [] }) },
  pages: { listPages: async () => ({ data: [] }) },
  dbases: { listDBases: async () => ({ data: [] }) },
  logs: { listLogs: async () => ({ data: [] }) },
};

// ── Top-level ─────────────────────────────────────────────────────────────────

function getMetadataData(params: any) { return sdk().metadata.listCatalogs(params); }
function deleteObject(params: any) { return Promise.resolve({ success: true }); }
function restoreObject(params: any) { return Promise.resolve({ success: true }); }
function loadCatalog(code: string) { return sdk().metadata.getCatalog({ id: code }); }
function loadThemes() { return Promise.resolve([]); }
function loadAllWidgetData() { return Promise.resolve({}); }

export const commands: any = {
  metadata: metadataCommands,
  modules: modulesCommands,
  center: centerCommands,
  bank: bankCommands,
  ...otherCommands,
  getEnumerationValues: metadataCommands.getEnumerationValues,
  callModuleFunction: metadataCommands.callModuleFunction,
  getFormDefs: async (params: any) => { const r = await sdk().metadata.listForms({}); return r.items || []; },
  getMetadataData,
  deleteObject,
  restoreObject,
  loadCatalog,
  loadThemes,
  loadAllWidgetData,
};

export { api };
export { useAuth } from './hooks/useAuth';
export type { ObjectModule, ManagerModule, ModuleExecutionResult } from './types';
export default commands;

